/**
 * VenuesManager - Venues CRUD with Radix UI components
 */

import { useState, useEffect, useRef, type FormEvent, type ChangeEvent } from 'react';
import axios from 'axios';
import { toast } from 'react-toastify';
import {
  Box,
  Flex,
  Grid,
  Card,
  Heading,
  Text,
  Button,
  Avatar,
  Link,
  Dialog,
  TextField,
  TextArea,
  Separator,
  Spinner,
} from '@radix-ui/themes';
import { fetchVenues, createVenue, updateVenue, deleteVenue } from '../../utils/api';
import type { Venue } from '../../types';
import PlanLimitCallout from '../Billing/PlanLimitCallout';
import { planLimitFromError, type PlanLimitDetails } from '../../utils/billing';

interface FormState {
  title: string;
  url: string;
  address: string;
  defaultPrizes: string;
  defaultRulesText: string;
  defaultStartTime: string;
  // Point defaults for new rounds. Empty = built-in default.
  defaultPointsPerQuestion: string;
  roundPoints: { round: string; points: string }[];
  logo: File | null;
  logoPreview: string | null;
}

// Form rows -> { "8": 3 }. Rows with a blank round or blank points are
// left out. The server checks the numbers.
export function roundPointsToMap(rows: { round: string; points: string }[]): Record<string, number> {
  const out: Record<string, number> = {};
  for (const { round, points } of rows) {
    if (!round.trim() || !points.trim()) continue;
    out[String(Number(round))] = Number(points);
  }
  return out;
}

const initialFormState: FormState = {
  title: '',
  url: '',
  address: '',
  defaultPrizes: '',
  defaultRulesText: '',
  defaultStartTime: '',
  defaultPointsPerQuestion: '',
  roundPoints: [],
  logo: null,
  logoPreview: null,
};

const VenuesManager: React.FC = () => {
  const [venues, setVenues] = useState<Venue[]>([]);
  const [loading, setLoading] = useState(true);
  const [isFormOpen, setIsFormOpen] = useState(false);
  const [editingVenue, setEditingVenue] = useState<Venue | null>(null);
  const [formData, setFormData] = useState<FormState>(initialFormState);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [formLimit, setFormLimit] = useState<PlanLimitDetails | null>(null);
  const [deleteTarget, setDeleteTarget] = useState<Venue | null>(null);
  const [isDeleting, setIsDeleting] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    loadVenues();
  }, []);

  useEffect(() => {
    return () => {
      // Cleanup blob URLs on unmount
      if (formData.logoPreview && !editingVenue?.logo) {
        URL.revokeObjectURL(formData.logoPreview);
      }
    };
  }, [formData.logoPreview, editingVenue]);

  const loadVenues = async (): Promise<void> => {
    setLoading(true);
    try {
      const response = await fetchVenues();
      setVenues(response.data ?? []);
    } catch (error) {
      console.error('Error loading venues:', error);
      toast.error('Failed to load venues');
    } finally {
      setLoading(false);
    }
  };

  const openCreateForm = (): void => {
    setEditingVenue(null);
    setFormData(initialFormState);
    setFormLimit(null);
    setIsFormOpen(true);
  };

  const openEditForm = (venue: Venue): void => {
    setEditingVenue(venue);
    setFormData({
      title: venue.name ?? '',
      url: venue.link ?? '',
      address: venue.address ?? '',
      defaultPrizes: venue.defaultPrizes ?? '',
      defaultRulesText: venue.defaultRulesText ?? '',
      defaultStartTime: venue.defaultStartTime ?? '',
      defaultPointsPerQuestion: venue.defaultPointsPerQuestion != null ? String(venue.defaultPointsPerQuestion) : '',
      roundPoints: Object.entries(venue.roundPointDefaults ?? {})
        .sort(([a], [b]) => Number(a) - Number(b))
        .map(([round, points]) => ({ round, points: String(points) })),
      logo: null,
      logoPreview: venue.logo ?? null,
    });
    setIsFormOpen(true);
  };

  const closeForm = (): void => {
    setIsFormOpen(false);
    setEditingVenue(null);
    if (formData.logoPreview && !editingVenue?.logo) {
      URL.revokeObjectURL(formData.logoPreview);
    }
    setFormData(initialFormState);
  };

  const handleInputChange = (
    e: ChangeEvent<HTMLInputElement | HTMLTextAreaElement>,
  ): void => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleFileChange = (e: ChangeEvent<HTMLInputElement>): void => {
    const file = e.target.files?.[0];
    if (!file) return;

    if (file.size > 5 * 1024 * 1024) {
      toast.error('File size must be less than 5MB');
      return;
    }

    if (!file.type.startsWith('image/')) {
      toast.error('Only image files are allowed');
      return;
    }

    const previewUrl = URL.createObjectURL(file);
    setFormData((prev) => ({
      ...prev,
      logo: file,
      logoPreview: previewUrl,
    }));
  };

  const removeLogo = (): void => {
    if (formData.logoPreview && !editingVenue?.logo) {
      URL.revokeObjectURL(formData.logoPreview);
    }
    setFormData((prev) => ({
      ...prev,
      logo: null,
      logoPreview: null,
    }));
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  const setRoundPoint = (index: number, field: 'round' | 'points', value: string): void => {
    setFormData((prev) => ({
      ...prev,
      roundPoints: prev.roundPoints.map((rp, i) => (i === index ? { ...rp, [field]: value } : rp)),
    }));
  };

  const addRoundPoint = (): void => {
    setFormData((prev) => ({ ...prev, roundPoints: [...prev.roundPoints, { round: '', points: '' }] }));
  };

  const removeRoundPoint = (index: number): void => {
    setFormData((prev) => ({ ...prev, roundPoints: prev.roundPoints.filter((_, i) => i !== index) }));
  };

  const handleSubmit = async (e: FormEvent<HTMLFormElement>): Promise<void> => {
    e.preventDefault();
    setIsSubmitting(true);

    try {
      const submitData = new FormData();
      submitData.append('title', formData.title);
      submitData.append('url', formData.url);
      submitData.append('address', formData.address);
      submitData.append('defaultPrizes', formData.defaultPrizes);
      submitData.append('defaultRulesText', formData.defaultRulesText);
      submitData.append('defaultStartTime', formData.defaultStartTime);
      submitData.append('defaultPointsPerQuestion', formData.defaultPointsPerQuestion.trim());
      submitData.append('roundPointDefaults', JSON.stringify(roundPointsToMap(formData.roundPoints)));
      if (formData.logo) {
        submitData.append('logo', formData.logo);
      }

      if (editingVenue) {
        await updateVenue(editingVenue.id, submitData);
        toast.success('Venue updated successfully');
      } else {
        await createVenue(submitData);
        toast.success('Venue created successfully');
      }

      closeForm();
      loadVenues();
    } catch (error) {
      console.error('Error saving venue:', error);
      const limit = planLimitFromError(error);
      if (limit) {
        setFormLimit(limit);
      } else {
        toast.error('Failed to save venue');
      }
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleDelete = async (): Promise<void> => {
    if (!deleteTarget) return;
    setIsDeleting(true);

    try {
      await deleteVenue(deleteTarget.id);
      toast.success('Venue deleted successfully');
      setDeleteTarget(null);
      loadVenues();
    } catch (error) {
      console.error('Error deleting venue:', error);
      const message =
        (axios.isAxiosError(error) && error.response?.data?.error) ||
        'Failed to delete venue';
      toast.error(message);
    } finally {
      setIsDeleting(false);
    }
  };

  return (
    <Flex direction="column" gap="4">
      <Flex justify="between" align="start" gap="3" wrap="wrap">
        <Box>
          <Heading size="6">Venues</Heading>
          <Text size="2" color="gray">
            Manage trivia venue locations
          </Text>
        </Box>
        <Button onClick={openCreateForm}>
          <PlusIcon /> Add Venue
        </Button>
      </Flex>

      {loading ? (
        <Card>
          <Flex align="center" gap="2">
            <Spinner />
            <Text color="gray">Loading venues...</Text>
          </Flex>
        </Card>
      ) : venues.length === 0 ? (
        <Card>
          <Flex direction="column" align="center" gap="2" py="6">
            <Text color="gray">No venues yet</Text>
            <Text size="2" color="gray" align="center" style={{ maxWidth: 480 }}>
              A venue is a bar or restaurant where you run trivia — game nights and the
              public schedule both need one.
            </Text>
            <Button variant="soft" onClick={openCreateForm}>
              <PlusIcon /> Add your first venue
            </Button>
          </Flex>
        </Card>
      ) : (
        <Grid columns={{ initial: '1', sm: '2', md: '3' }} gap="4">
          {venues.map((venue) => (
            <Card key={venue.id}>
              <Flex direction="column" gap="3" height="100%">
                <Flex gap="3" align="center">
                  <Avatar
                    size="4"
                    radius="medium"
                    src={venue.logo ?? undefined}
                    fallback={<LocationIcon />}
                    alt={`${venue.name} logo`}
                  />
                  <Flex direction="column" gap="1">
                    <Text weight="bold">{venue.name}</Text>
                    {venue.link && (
                      <Link href={venue.link} target="_blank" rel="noopener noreferrer" size="2">
                        <Flex as="span" align="center" gap="1">
                          <ExternalLinkIcon />
                          Visit Website
                        </Flex>
                      </Link>
                    )}
                  </Flex>
                </Flex>
                <Flex gap="2" justify="end" mt="auto">
                  <Button size="1" variant="soft" onClick={() => openEditForm(venue)}>
                    Edit
                  </Button>
                  <Button size="1" variant="soft" color="red" onClick={() => setDeleteTarget(venue)}>
                    Delete
                  </Button>
                </Flex>
              </Flex>
            </Card>
          ))}
        </Grid>
      )}

      {/* Create/Edit Venue Dialog */}
      <Dialog.Root open={isFormOpen} onOpenChange={(open) => !open && closeForm()}>
        <Dialog.Content maxWidth="520px">
          <Dialog.Title>{editingVenue ? 'Edit Venue' : 'Add New Venue'}</Dialog.Title>
          <Dialog.Description size="2" color="gray" mb="4">
            {editingVenue
              ? 'Update the venue information below.'
              : 'Add a new trivia venue to your network.'}
          </Dialog.Description>

          <form onSubmit={handleSubmit}>
            <Flex direction="column" gap="3">
              <label>
                <Text as="div" size="2" weight="medium" mb="1">
                  Venue Name
                </Text>
                <TextField.Root
                  name="title"
                  placeholder="e.g., The Local Pub"
                  value={formData.title}
                  onChange={handleInputChange}
                  required
                />
              </label>

              <label>
                <Text as="div" size="2" weight="medium" mb="1">
                  Website URL
                </Text>
                <TextField.Root
                  type="url"
                  name="url"
                  placeholder="https://example.com"
                  value={formData.url}
                  onChange={handleInputChange}
                  required
                />
              </label>

              <label>
                <Text as="div" size="2" weight="medium" mb="1">
                  Address
                </Text>
                <TextField.Root
                  name="address"
                  placeholder="e.g., 123 Main St, Grand Junction, CO 81501"
                  value={formData.address}
                  onChange={handleInputChange}
                />
              </label>

              <label>
                <Text as="div" size="2" weight="medium" mb="1">
                  Default Start Time
                </Text>
                <TextField.Root
                  name="defaultStartTime"
                  placeholder="e.g., 7:00 PM"
                  value={formData.defaultStartTime}
                  onChange={handleInputChange}
                />
                <Text as="div" size="1" color="gray" mt="1">
                  Shown on the welcome slide unless an event overrides it.
                </Text>
              </label>

              <label>
                <Text as="div" size="2" weight="medium" mb="1">
                  Default Prizes
                </Text>
                <TextArea
                  name="defaultPrizes"
                  rows={3}
                  placeholder="e.g., $20 1st · $15 2nd · Drawing"
                  value={formData.defaultPrizes}
                  onChange={handleInputChange}
                />
                <Text as="div" size="1" color="gray" mt="1">
                  One prize per line (or separated by · or |).
                </Text>
              </label>

              <label>
                <Text as="div" size="2" weight="medium" mb="1">
                  Default Rules
                </Text>
                <TextArea
                  name="defaultRulesText"
                  rows={4}
                  placeholder="One rule per line"
                  value={formData.defaultRulesText}
                  onChange={handleInputChange}
                />
                <Text as="div" size="1" color="gray" mt="1">
                  One rule per line. Used for the rules slide unless an event overrides it.
                </Text>
              </label>

              <Box>
                <Text as="div" size="2" weight="medium" mb="1">
                  Points
                </Text>
                <Text as="div" size="1" color="gray" mb="2">
                  New rounds in this venue&apos;s game nights start with these points per question.
                  Existing rounds do not change. The host can still change any round in the quiz editor.
                </Text>
                <Flex align="center" gap="2" mb="2">
                  <Text as="label" size="2" htmlFor="venue-default-points" style={{ minWidth: 150 }}>
                    Default per question
                  </Text>
                  <TextField.Root
                    id="venue-default-points"
                    name="defaultPointsPerQuestion"
                    type="number"
                    min={1}
                    max={100}
                    placeholder="2"
                    value={formData.defaultPointsPerQuestion}
                    onChange={handleInputChange}
                    style={{ width: 90 }}
                  />
                </Flex>
                {formData.roundPoints.map((rp, i) => (
                  <Flex align="center" gap="2" mb="2" key={i}>
                    <Text size="2" style={{ minWidth: 150 }}>Round</Text>
                    <TextField.Root
                      type="number"
                      min={1}
                      max={50}
                      aria-label="Round number"
                      placeholder="8"
                      value={rp.round}
                      onChange={(e) => setRoundPoint(i, 'round', e.target.value)}
                      style={{ width: 70 }}
                    />
                    <Text size="2">=</Text>
                    <TextField.Root
                      type="number"
                      min={1}
                      max={100}
                      aria-label={`Points for round ${rp.round || i + 1}`}
                      placeholder="3"
                      value={rp.points}
                      onChange={(e) => setRoundPoint(i, 'points', e.target.value)}
                      style={{ width: 70 }}
                    />
                    <Text size="2" color="gray">pts</Text>
                    <Button type="button" size="1" variant="soft" color="gray" onClick={() => removeRoundPoint(i)}>
                      Remove
                    </Button>
                  </Flex>
                ))}
                <Button type="button" size="1" variant="soft" onClick={addRoundPoint}>
                  + Round override
                </Button>
              </Box>

              <Box>
                <Text as="div" size="2" weight="medium" mb="1">
                  Logo
                </Text>
                <input
                  type="file"
                  accept="image/*"
                  onChange={handleFileChange}
                  ref={fileInputRef}
                />
                <Text as="div" size="1" color="gray" mt="1">
                  Max file size: 5MB
                </Text>

                {formData.logoPreview && (
                  <Flex align="center" gap="3" mt="2">
                    <Avatar
                      size="5"
                      radius="medium"
                      src={formData.logoPreview}
                      fallback={<LocationIcon />}
                      alt="Logo preview"
                    />
                    <Button type="button" size="1" variant="soft" color="gray" onClick={removeLogo}>
                      Remove Image
                    </Button>
                  </Flex>
                )}
              </Box>
            </Flex>

            <Separator size="4" my="4" />

            {formLimit && <Box mb="3"><PlanLimitCallout details={formLimit} /></Box>}

            <Flex gap="3" justify="end">
              <Button type="button" variant="soft" color="gray" onClick={closeForm}>
                Cancel
              </Button>
              <Button type="submit" disabled={isSubmitting}>
                {isSubmitting
                  ? 'Saving...'
                  : editingVenue
                  ? 'Update Venue'
                  : 'Create Venue'}
              </Button>
            </Flex>
          </form>
        </Dialog.Content>
      </Dialog.Root>

      {/* Delete Confirmation Dialog */}
      <Dialog.Root open={!!deleteTarget} onOpenChange={(open) => !open && setDeleteTarget(null)}>
        <Dialog.Content maxWidth="420px">
          <Dialog.Title>Delete Venue</Dialog.Title>
          <Dialog.Description size="2" color="gray">
            Are you sure you want to delete &ldquo;{deleteTarget?.name}&rdquo;? Venues with
            events, live events, or games can&apos;t be deleted until those are removed.
          </Dialog.Description>
          <Flex gap="3" mt="4" justify="end">
            <Dialog.Close>
              <Button variant="soft" color="gray">
                Cancel
              </Button>
            </Dialog.Close>
            <Button color="red" onClick={handleDelete} disabled={isDeleting}>
              {isDeleting ? 'Deleting...' : 'Delete'}
            </Button>
          </Flex>
        </Dialog.Content>
      </Dialog.Root>
    </Flex>
  );
};

// Icons
const PlusIcon: React.FC = () => (
  <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
    <path
      d="M8 3V13M3 8H13"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
    />
  </svg>
);

const LocationIcon: React.FC = () => (
  <svg width="24" height="24" viewBox="0 0 16 16" fill="none">
    <path
      d="M13 6.66667C13 10.6667 8 14.6667 8 14.6667C8 14.6667 3 10.6667 3 6.66667C3 5.34058 3.52678 4.06881 4.46447 3.13113C5.40215 2.19345 6.67392 1.66667 8 1.66667C9.32608 1.66667 10.5979 2.19345 11.5355 3.13113C12.4732 4.06881 13 5.34058 13 6.66667Z"
      stroke="currentColor"
      strokeWidth="1.5"
    />
    <circle cx="8" cy="6.66667" r="2" stroke="currentColor" strokeWidth="1.5" />
  </svg>
);

const ExternalLinkIcon: React.FC = () => (
  <svg width="14" height="14" viewBox="0 0 16 16" fill="none">
    <path
      d="M12 8.66667V12.6667C12 13.0203 11.8595 13.3594 11.6095 13.6095C11.3594 13.8595 11.0203 14 10.6667 14H3.33333C2.97971 14 2.64057 13.8595 2.39052 13.6095C2.14048 13.3594 2 13.0203 2 12.6667V5.33333C2 4.97971 2.14048 4.64057 2.39052 4.39052C2.64057 4.14048 2.97971 4 3.33333 4H7.33333"
      stroke="currentColor"
      strokeWidth="1.5"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
    <path
      d="M10 2H14V6"
      stroke="currentColor"
      strokeWidth="1.5"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
    <path
      d="M6.66667 9.33333L14 2"
      stroke="currentColor"
      strokeWidth="1.5"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </svg>
);

export default VenuesManager;
